// src/components/Layout/ResponsiveGrid.jsx
import React from 'react';
import styled from 'styled-components';

// Define standard breakpoints similar to Bootstrap
const breakpoints = {
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1400px'
};

// Container component that behaves like Bootstrap's container
const Container = styled.div`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  padding-right: 1rem;
  padding-left: 1rem;
  
  @media (min-width: ${breakpoints.sm}) {
    max-width: 540px;
  }
  
  @media (min-width: ${breakpoints.md}) {
    max-width: 720px;
  }
  
  @media (min-width: ${breakpoints.lg}) {
    max-width: 960px;
  }
  
  @media (min-width: ${breakpoints.xl}) {
    max-width: 1140px;
  }
  
  @media (min-width: ${breakpoints.xxl}) {
    max-width: 1320px;
  }
  
  ${props => props.fluid && `
    max-width: 100%;
  `}
`;

// Row component similar to Bootstrap's row
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: -0.5rem;
  margin-left: -0.5rem;
  
  ${props => props.noGutters && `
    margin-right: 0;
    margin-left: 0;
  `}
`;

// Column component with responsive width based on props
const Col = styled.div`
  position: relative;
  width: 100%;
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  
  ${props => props.xs && `
    flex: 0 0 ${(props.xs / 12) * 100}%;
    max-width: ${(props.xs / 12) * 100}%;
  `}
  
  @media (min-width: ${breakpoints.sm}) {
    ${props => props.sm && `
      flex: 0 0 ${(props.sm / 12) * 100}%;
      max-width: ${(props.sm / 12) * 100}%;
    `}
  }
  
  @media (min-width: ${breakpoints.md}) {
    ${props => props.md && `
      flex: 0 0 ${(props.md / 12) * 100}%;
      max-width: ${(props.md / 12) * 100}%;
    `}
  }
  
  @media (min-width: ${breakpoints.lg}) {
    ${props => props.lg && `
      flex: 0 0 ${(props.lg / 12) * 100}%;
      max-width: ${(props.lg / 12) * 100}%;
    `}
  }
  
  @media (min-width: ${breakpoints.xl}) {
    ${props => props.xl && `
      flex: 0 0 ${(props.xl / 12) * 100}%;
      max-width: ${(props.xl / 12) * 100}%;
    `}
  }
`;

// Spacing utility component
const Spacing = styled.div`
  ${props => props.mb && `margin-bottom: ${props.mb}rem;`}
  ${props => props.mt && `margin-top: ${props.mt}rem;`}
  ${props => props.ml && `margin-left: ${props.ml}rem;`}
  ${props => props.mr && `margin-right: ${props.mr}rem;`}
  ${props => props.mx && `
    margin-left: ${props.mx}rem;
    margin-right: ${props.mx}rem;
  `}
  ${props => props.my && `
    margin-top: ${props.my}rem;
    margin-bottom: ${props.my}rem;
  `}
  ${props => props.m && `margin: ${props.m}rem;`}
  
  ${props => props.pb && `padding-bottom: ${props.pb}rem;`}
  ${props => props.pt && `padding-top: ${props.pt}rem;`}
  ${props => props.pl && `padding-left: ${props.pl}rem;`}
  ${props => props.pr && `padding-right: ${props.pr}rem;`}
  ${props => props.px && `
    padding-left: ${props.px}rem;
    padding-right: ${props.px}rem;
  `}
  ${props => props.py && `
    padding-top: ${props.py}rem;
    padding-bottom: ${props.py}rem;
  `}
  ${props => props.p && `padding: ${props.p}rem;`}
  
  @media (max-width: ${breakpoints.md}) {
    ${props => props.mbMobile && `margin-bottom: ${props.mbMobile}rem;`}
    ${props => props.mtMobile && `margin-top: ${props.mtMobile}rem;`}
    ${props => props.myMobile && `
      margin-top: ${props.myMobile}rem;
      margin-bottom: ${props.myMobile}rem;
    `}
    ${props => props.mMobile && `margin: ${props.mMobile}rem;`}
    
    ${props => props.pbMobile && `padding-bottom: ${props.pbMobile}rem;`}
    ${props => props.ptMobile && `padding-top: ${props.ptMobile}rem;`}
    ${props => props.pyMobile && `
      padding-top: ${props.pyMobile}rem;
      padding-bottom: ${props.pyMobile}rem;
    `}
    ${props => props.pMobile && `padding: ${props.pMobile}rem;`}
  }
`;

// Hidden component to show/hide elements based on screen size
const Hidden = styled.div`
  ${props => props.xs && `
    @media (max-width: ${breakpoints.sm}) {
      display: none;
    }
  `}
  
  ${props => props.sm && `
    @media (max-width: ${breakpoints.md}) {
      display: none;
    }
  `}
  
  ${props => props.md && `
    @media (max-width: ${breakpoints.lg}) {
      display: none;
    }
  `}
  
  ${props => props.lg && `
    @media (max-width: ${breakpoints.xl}) {
      display: none;
    }
  `}
  
  ${props => props.xl && `
    @media (max-width: ${breakpoints.xxl}) {
      display: none;
    }
  `}
  
  ${props => props.smUp && `
    @media (min-width: ${breakpoints.sm}) {
      display: none;
    }
  `}
  
  ${props => props.mdUp && `
    @media (min-width: ${breakpoints.md}) {
      display: none;
    }
  `}
  
  ${props => props.lgUp && `
    @media (min-width: ${breakpoints.lg}) {
      display: none;
    }
  `}
  
  ${props => props.xlUp && `
    @media (min-width: ${breakpoints.xl}) {
      display: none;
    }
  `}
`;

// Export all components
export {
  Container,
  Row,
  Col,
  Spacing,
  Hidden,
  breakpoints
};